import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import WarningIcon from '@mui/icons-material/Warning';
import { useTheme, alpha } from '@mui/material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link } from 'react-router-dom';

type LimitType = 'clients' | 'seats' | 'storage' | 'aiCredits' | 'custom';
type WarningVariant = 'inline' | 'alert' | 'progress' | 'compact';
type SeverityLevel = 'info' | 'warning' | 'error';

export interface UsageLimitWarningProps {
  variant?: WarningVariant;
  limitType?: LimitType;
  used: number;
  entitled: number;
  customLabel?: string;
  showUpgradeButton?: boolean;
  warningThreshold?: number; // Percentage (default 80)
  criticalThreshold?: number; // Percentage (default 95)
  onUpgradeClick?: () => void;
}

/**
 * UsageLimitWarning Component (HA-109)
 * Shows usage warnings when approaching limits (80%+)
 *
 * Variants:
 * - inline: Simple text with icon
 * - alert: Full MUI Alert component
 * - progress: Progress bar visualization
 * - compact: Minimal badge-style indicator
 */
const UsageLimitWarning: React.FC<UsageLimitWarningProps> = ({
  variant = 'progress',
  limitType = 'custom',
  used,
  entitled,
  customLabel,
  showUpgradeButton = true,
  warningThreshold = 80,
  criticalThreshold = 95,
  onUpgradeClick,
}) => {
  const theme = useTheme();

  // Calculate usage percentage
  const percentage = entitled > 0 ? Math.round((used / entitled) * 100) : 0;
  const isAtLimit = used >= entitled;
  const isCritical = percentage >= criticalThreshold;
  const isWarning = percentage >= warningThreshold;

  // Determine severity
  const severity: SeverityLevel =
    isAtLimit || isCritical ? 'error' : isWarning ? 'warning' : 'info';

  // Get label based on limit type
  const getLabel = (): string => {
    if (customLabel) return customLabel;
    switch (limitType) {
      case 'clients':
        return 'clients';
      case 'seats':
        return 'team seats';
      case 'storage':
        return 'storage';
      case 'aiCredits':
        return 'AI credits';
      default:
        return 'items';
    }
  };

  const label = getLabel();

  // Get color based on severity
  const getColor = () => {
    switch (severity) {
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      default:
        return theme.palette.info.main;
    }
  };

  const color = getColor();

  // Get icon based on severity
  const getIcon = () => {
    switch (severity) {
      case 'error':
        return <ErrorIcon fontSize="small" />;
      case 'warning':
        return <WarningIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  // Get message
  const getMessage = (): string => {
    if (isAtLimit) {
      return `You've reached your ${label} limit`;
    }
    if (isCritical) {
      return `Almost at your ${label} limit`;
    }
    if (isWarning) {
      return `Approaching your ${label} limit`;
    }

    return `${label} usage`;
  };

  // Don't show if below warning threshold and not explicitly showing info
  if (!isWarning && variant !== 'progress') {
    return null;
  }

  // Compact variant
  if (variant === 'compact') {
    if (!isWarning) return null;

    return (
      <Tooltip title={`${used} of ${entitled} ${label} used`}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: alpha(color, 0.1),
            color,
          }}
        >
          {getIcon()}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
            }}
          >
            {percentage}%
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  // Inline variant
  if (variant === 'inline') {
    if (!isWarning) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color,
          }}
        >
          {getIcon()}
          <Typography variant="body2">
            {used} of {entitled} {label} used
          </Typography>
        </Box>
        {showUpgradeButton && (
          <Button
            component={Link}
            to="/subscription"
            variant="text"
            size="small"
            color={severity === 'error' ? 'error' : severity === 'warning' ? 'warning' : 'primary'}
            onClick={onUpgradeClick}
          >
            Upgrade for more
          </Button>
        )}
      </Box>
    );
  }

  // Alert variant
  if (variant === 'alert') {
    if (!isWarning) return null;

    return (
      <Alert
        severity={severity}
        action={
          showUpgradeButton && (
            <Button
              component={Link}
              to="/subscription"
              color="inherit"
              size="small"
              startIcon={<UpgradeIcon />}
              onClick={onUpgradeClick}
            >
              Upgrade
            </Button>
          )
        }
      >
        <AlertTitle>{getMessage()}</AlertTitle>
        You&apos;ve used {used} of {entitled} {label}.
        {isAtLimit
          ? ' Upgrade your plan to add more.'
          : ` Consider upgrading to avoid hitting your limit.`}
      </Alert>
    );
  }

  // Progress variant (default)
  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isWarning && (
            <Box component="span" sx={{ color, display: 'flex' }}>
              {getIcon()}
            </Box>
          )}
          <Typography variant="body2" color={isWarning ? color : 'text.secondary'}>
            {getMessage()}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          color={isWarning ? color : 'text.primary'}
          sx={{
            fontWeight: 600,
          }}
        >
          {used} / {entitled === -1 ? 'Unlimited' : entitled}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(percentage, 100)}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: alpha(color, 0.15),
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            backgroundColor: color,
          },
        }}
      />
      {isWarning && showUpgradeButton && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            component={Link}
            to="/subscription"
            variant="text"
            size="small"
            startIcon={<UpgradeIcon />}
            onClick={onUpgradeClick}
            sx={{ color }}
          >
            {isAtLimit ? 'Upgrade to add more' : 'Upgrade for unlimited'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default UsageLimitWarning;
